import TefsirBody from '@components/shared/Tefsir/TefsirBody'
import TefsirToggle from '@components/shared/Tefsir/TefsirToggle'
import { useAyahTefsir } from '@hooks/useAyahTefsir'
import type { ResolvedBundledTefsir } from 'helpers/quranPageTefsir'
import { useState } from 'react'

type AyahTefsirProps = {
  surahId: number
  verseNumber: number
  /**
   * Tefsir bundled with the page (mobile app, works offline). When missing
   * (web, older app data) the tefsir is fetched from the API instead.
   */
  bundledTefsir?: ResolvedBundledTefsir | null
}

/**
 * Collapsible Diyanet tefsir inside the reader's meal popup. Shows the bundled
 * tefsir when the page has it; otherwise fetches this ayah's tefsir only when
 * the section is opened for the first time.
 */
const AyahTefsir = ({ surahId, verseNumber, bundledTefsir }: AyahTefsirProps) => {
  const [isOpen, setIsOpen] = useState(false)
  // Once opened, keep the request enabled so collapsing doesn't drop the data.
  const [hasBeenOpened, setHasBeenOpened] = useState(false)
  const shouldFetch = hasBeenOpened && !bundledTefsir
  const fetched = useAyahTefsir(surahId, verseNumber, shouldFetch)

  // One shape for both sources, so rendering doesn't care where it came from.
  const tefsir = bundledTefsir
    ? bundledTefsir
    : fetched.tefsir && {
        range: fetched.tefsir.tefsir_range,
        html: fetched.tefsir.tefsir_html ?? '',
      }

  /** Opens or closes the section and starts the fetch on first open. */
  const handleToggle = () => {
    setIsOpen((open) => !open)
    setHasBeenOpened(true)
  }

  /** Picks what to show inside the opened section for the current state. */
  const renderContent = () => {
    if (!bundledTefsir && fetched.isLoading) {
      return <p className="mt-3 text-sm text-muted">Tefsir yükleniyor...</p>
    }
    if (!tefsir) {
      return <p className="mt-3 text-sm text-muted">Tefsir yüklenemedi.</p>
    }
    if (!tefsir.html.trim()) {
      return <p className="mt-3 text-sm text-muted">Bu ayet için tefsir bulunmuyor.</p>
    }
    // Limit the height so the popup's action buttons stay reachable.
    return <TefsirBody html={tefsir.html} className="max-h-[50vh] overflow-y-auto" />
  }

  return (
    <div className="border-t border-main mt-3 pt-3">
      <TefsirToggle isOpen={isOpen} onToggle={handleToggle} verseRange={tefsir?.range} />
      {isOpen && renderContent()}
    </div>
  )
}

export default AyahTefsir
